import { convertToBoolean } from 'src/utils'
import {
  customerContainer,
  checkoutContainer,
  orderHistoryContainer,
  accountsContainer,
} from 'src/models'
import { storeContainer } from 'src/models/Store'
import { getCookie, APPConfig } from 'config/appConfig'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import {
  isGuestCheckoutEnabledForWeb,
  checkMysiteOrPersonalOffer,
  isToEnableEQGuestFastFlow,
  isFeatureEnabled,
} from 'src/utils'

// Types of Address Forms
export const addressFormTypes = [
  'addressBook',
  'payment',
  'checkout',
  'subscription',
  'accountProfile',
  'okta',
  'convertAccount',
  'legalAddress',
]

// User Types
export const userTypes = ['registeredUser', 'guest']

// Postal Lookup Countries
export const postalLookUpCountries = [
  'MX',
  'BN',
  'ID',
  'JP',
  'MY',
  'TW',
  'VE',
  'TH',
]

// FieldJson for US and CA - To be removed after refactoring Addresses folder
export const fieldJsonForUSCA = () => {
  return [
    {
      name: 'addressLine1',
      isAddressField: true,
    },
    {
      name: 'addressLine2',
      isAddressField: true,
    },
    {
      name: 'city',
      isAddressField: true,
    },
    {
      name: 'state',
      isAddressField: true,
    },
    {
      name: 'zip',
      isAddressField: true,
    },
  ]
}

// To dynamically import form fields of a country
export const getAddressFieldsJsonBasedOnCountry = async (country = 'US') => {
  let formFieldsForUSCA = fieldJsonForUSCA() || []
  let addressFormFields = []
  try {
    const module = await import(
      `src/views/components/CommonFormForCS/AddressForms/${country}.js`
    )
    let renderFormFields = module.default || module
    addressFormFields = renderFormFields() || []
  } catch (e) {
    addressFormFields = formFieldsForUSCA
    console.log('Market Field JSON not found', e)
  }
  return addressFormFields
}

// To get address form fields based on country
export const getAddressFormFieldsBasedOnFormType = async (
  fields = [],
  type = '',
  isToHideLegalAddressRadioOptions = false
) => {
  let addressFormFields = []
  let isRegisteredUser = customerContainer.isRegisterUser
    ? 'registeredUser'
    : 'guest'
  let isToShowLegalAddressRadioOptions =
    isToShowLegalAddressOptionInAddressForm() || false
  let { isToShowVipCheckbox = false } = isToShowVIPGuestCheckoutCheckbox()
  let isToShowGuestCommunicationCheckboxes =
    isToShowCommunicationOptInCheckbox()
  let guestCheckoutCommunicationCheckboxes = [
    'guestPromotionCheckbox',
    'smsOptIn',
    'emailOptIn',
  ]
  addressFormFields = await fields?.reduce((consolidatedFormFields, field) => {
    let formType = type || ''
    const {
      componentToRender = [],
      isToHideField = 'false',
      supportedUserType = [],
      name = '',
    } = field || {}

    // Render Form Fields based on the Form Type
    let supportedFormTypes =
      componentToRender?.length > 0 ? componentToRender : addressFormTypes
    let isFormTypeSupported = supportedFormTypes?.includes(formType)

    // To hide fields if not supported
    let isToHideFormField = convertToBoolean(isToHideField)

    // Render Form Field based on the User Type
    let availableUserTypes =
      supportedUserType?.length > 0 ? supportedUserType : userTypes
    let isUserTypeSupported = availableUserTypes?.includes(isRegisteredUser)

    let isToShowSelectedField =
      isFormTypeSupported && !isToHideFormField && isUserTypeSupported

    if (isToShowSelectedField) {
      if (name === 'legalAddressSameAsShippingAddress') {
        const isToShowLegalAddressOptions =
          !checkoutContainer.isEditAddressFormEnabled &&
          isToShowLegalAddressRadioOptions &&
          !isToHideLegalAddressRadioOptions
        isToShowLegalAddressOptions && consolidatedFormFields.push(field)
      } else if (name === 'signupBrandRepresentative') {
        isToShowVipCheckbox && consolidatedFormFields.push(field)
      } else if (guestCheckoutCommunicationCheckboxes?.includes(name)) {
        isToShowGuestCommunicationCheckboxes &&
          consolidatedFormFields.push(field)
      } else {
        consolidatedFormFields.push(field)
      }
    }
    return consolidatedFormFields
  }, [])
  return addressFormFields
}

/**
 * Retrieves an array of address-only form fields for validation.
 * User related fields, COmmunication related fields and checkboxes will not be included in the returned array.
 *
 * @param {Array} fields - An array of form fields.
 * @param {string} [type=''] - The type of the form.
 * @returns {Promise<Array>} - An array of address-only form fields.
 */
export const getAddressOnlyFieldsForValidation = async (
  fields = [],
  type = ''
) => {
  let addressOnlyFormFields = []
  addressOnlyFormFields = await fields?.reduce(
    (consolidatedFormFields, field) => {
      let formType = type || ''
      const {
        componentToRender = [],
        isToHideField = 'false',
        supportedUserType = [],
        isAddressField = false,
        name = '',
      } = field || {}

      // Render Form Fields based on the Form Type
      let supportedFormTypes =
        componentToRender?.length > 0 ? componentToRender : addressFormTypes
      let isFormTypeSupported = supportedFormTypes?.includes(formType)

      // To hide fields if not supported
      let isToHideFormField = convertToBoolean(isToHideField)

      // Render Form Field based on the User Type
      let isRegisteredUser = customerContainer.isRegisterUser
        ? 'registeredUser'
        : 'guest'

      let availableUserTypes =
        supportedUserType?.length > 0 ? supportedUserType : userTypes
      let isUserTypeSupported = availableUserTypes?.includes(isRegisteredUser)

      let isToShowSelectedField =
        isFormTypeSupported &&
        !isToHideFormField &&
        isUserTypeSupported &&
        isAddressField

      if (isToShowSelectedField) {
        consolidatedFormFields.push(name)
      }
      return consolidatedFormFields
    },
    []
  )
  return addressOnlyFormFields
}

export const getFieldAliasName = (fieldName = '') => {
  let fieldNameAlias

  if (fieldName === 'stateList') {
    fieldNameAlias = 'state'
  } else if (fieldName === 'wardList') {
    fieldNameAlias = 'city'
  } else if (fieldName === 'districtList' || fieldName === 'district') {
    fieldNameAlias = 'county'
  } else if (fieldName === 'postalCode') {
    fieldNameAlias = 'zip'
  } else {
    fieldNameAlias = fieldName
  }

  return fieldNameAlias
}

// To check if all fields are empty
export const checkIfFormIsEmpty = (formFields = [], formData = {}) => {
  let isFormFieldHasValue = false
  if (Object.keys(formData)?.length > 0 && formFields?.length > 0) {
    isFormFieldHasValue = formFields?.every(fieldName => {
      let fieldNameAlias = getFieldAliasName(fieldName) || ''
      let fieldValue = formData[fieldName] || formData[fieldNameAlias] || ''
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ''
      )
    })
  }
  return isFormFieldHasValue
}

// To compare two form field values
export const checkIfTwoFormValuesAreEqual = (
  formFields,
  formData,
  savedFormData
) => {
  let isFormValuesEqual = true
  if (
    Object.keys(formData)?.length > 0 &&
    Object.keys(savedFormData)?.length > 0 &&
    formFields?.length > 0
  ) {
    isFormValuesEqual = formFields?.every(fieldName => {
      let fieldNameAlias = getFieldAliasName(fieldName)
      let fieldValue = formData[fieldName] || formData[fieldNameAlias] || ''
      let savedFieldValue =
        savedFormData[fieldName] || savedFormData[fieldNameAlias] || ''
      return fieldValue.toLowerCase() === savedFieldValue.toLowerCase()
    })
  } else {
    isFormValuesEqual = false
  }
  return isFormValuesEqual
}

/**
 * Legal address visibility also depends on the `legalAddressCheckinCheckout`
 * master switch, enforced through `isAccountTypeEligibleForLegalAddress()`.
 */
export const isToShowLegalAddressChanges = () => {
  const curLocale =
    getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
    }) || ''
  const homeCountry = customerContainer.getUserHomeCountry() || ''
  const activeCountry = curLocale?.includes('_')
    ? curLocale?.split?.('_')?.[1]
    : curLocale?.split?.('-')?.[1]
  const isHomeCountry = homeCountry === activeCountry
  const isRegisteredUser = customerContainer.isRegisterUser || false

  return (
    isRegisteredUser && isHomeCountry && isAccountTypeEligibleForLegalAddress()
  )
}

// Currently this function is being used to hide the legal address accordion from checkout page
//  for US and CA markets.Add Other markets based on requirement.
export const shouldHideLegalAddressInCheckout = () => {
  const propertyName = 'hidelegaladdressaccordionincheckout'

  const findProperty = response =>
    response?.properties?.find(
      prop => prop?.name?.toLowerCase() === propertyName
    )?.value

  const hideLegalAddressAccordion =
    findProperty(storeContainer.activeStoreResponse) ??
    findProperty(storeContainer.globalStoreResponse) ??
    false

  return convertToBoolean(hideLegalAddressAccordion)
}

export const isToShowLegalAddressOptionInAddressForm = () => {
  const showLegalAddressAccordion =
    convertToBoolean(getCookie('isToShowLegalAddressAccordion')) || false
  let isLegalAddressApplicable = isToShowLegalAddressChanges() || false
  return (
    isLegalAddressApplicable &&
    (!checkoutContainer.isUserHasLegalAddress || showLegalAddressAccordion)
  )
}

export const hasUserAccountAddress = () => {
  return accountsContainer?.profileResponse?.addresses?.length > 0 || false
}

export const isToShowVIPGuestCheckoutCheckbox = () => {
  let isGuestEnabled = isGuestCheckoutEnabledForWeb()
  let poMysiteContext = checkMysiteOrPersonalOffer()
  let enableEQFastFlow = isToEnableEQGuestFastFlow()
  let isToShowVipCheckbox = isGuestEnabled && !poMysiteContext
  let isToShowContentFromContentStack = isToShowVipCheckbox && enableEQFastFlow
  return {
    isToShowVipCheckbox,
    isToShowContentFromContentStack,
  }
}

/** Shared gate for both guest-checkout Ironclad pending checks below. */
const isGuestCheckoutIroncladFlowActive = () =>
  !customerContainer?.isRegisterUser &&
  isFeatureEnabled('enableGuestCheckoutCheckboxes') &&
  convertToBoolean(APPConfig?.getAppConfig()?.FEATURE_IRONCLAD_IMPLEMENTATION)

/**
 * True when GuestCheckoutCheckboxes' Ironclad legal agreement checkbox is
 * shown (guest + enableGuestCheckoutCheckboxes + FEATURE_IRONCLAD_IMPLEMENTATION)
 * but not yet checked. Used to keep the shipping accordion expanded/blocked
 * and the Submit Order button disabled until the guest agrees.
 */
export const isGuestLegalAgreementPending = () => {
  return (
    isGuestCheckoutIroncladFlowActive() &&
    !accountsContainer.isGuestCheckoutLegalAgreementChecked
  )
}

/**
 * True when guest checkout's Ironclad checkbox flow is active and either:
 * the checkbox is unchecked (mandatory to place a guest order), or it is
 * checked but the guest has not yet clicked Continue and had the shipping
 * address save succeed (ShippingAccordion.handleAddressContinue). Unlike
 * isGuestLegalAgreementPending, checking the box alone does not clear this.
 * Used solely to gate the Submit Order button.
 */
export const isGuestSubmitOrderPending = () => {
  return (
    isGuestCheckoutIroncladFlowActive() &&
    (!accountsContainer.isGuestCheckoutLegalAgreementChecked ||
      !accountsContainer.isGuestCheckoutShippingAddressConfirmed)
  )
}

export const isToShowCommunicationOptInCheckbox = () => {
  let isGuestEnabled = isGuestCheckoutEnabledForWeb()
  let poMysiteContext = checkMysiteOrPersonalOffer()
  let enableEQFastFlow = isToEnableEQGuestFastFlow()
  let isToShowCommunicationCheckboxes =
    isGuestEnabled && !poMysiteContext && enableEQFastFlow
  return isToShowCommunicationCheckboxes
}

const findLegalAddressPropertyValue = (propertyName, defaultValue = false) => {
  return (
    storeContainer.activeStoreResponse?.properties?.find(
      prop => prop?.name?.toLowerCase() === propertyName.toLowerCase()
    )?.value ||
    storeContainer.globalStoreResponse?.properties?.find(
      prop => prop?.name?.toLowerCase() === propertyName.toLowerCase()
    )?.value ||
    defaultValue
  )
}

const normalizeAccountTypesArray = accountTypes => {
  return accountTypes
    .filter(type => type != null)
    .map(type => String(type).trim())
    .filter(Boolean)
}

const parseAllowedAccountTypesConfig = allowedAccountTypes => {
  if (Array.isArray(allowedAccountTypes)) {
    return normalizeAccountTypesArray(allowedAccountTypes)
  }

  const trimmedAccountTypes = allowedAccountTypes?.trim?.() || ''
  if (!trimmedAccountTypes) {
    return []
  }

  if (
    trimmedAccountTypes.startsWith('[') &&
    trimmedAccountTypes.endsWith(']')
  ) {
    try {
      const parsedAccountTypes = JSON.parse(trimmedAccountTypes)
      return Array.isArray(parsedAccountTypes)
        ? normalizeAccountTypesArray(parsedAccountTypes)
        : trimmedAccountTypes
    } catch {
      return trimmedAccountTypes
    }
  }

  return trimmedAccountTypes.includes(',')
    ? normalizeAccountTypesArray(trimmedAccountTypes.split(','))
    : trimmedAccountTypes
}

const isAllowedAccountTypesConfigEmpty = allowedAccountTypesConfig => {
  return (
    !allowedAccountTypesConfig ||
    (Array.isArray(allowedAccountTypesConfig) &&
      allowedAccountTypesConfig.length === 0) ||
    (!Array.isArray(allowedAccountTypesConfig) &&
      allowedAccountTypesConfig.trim() === '')
  )
}

const isMatchingAllowedAccountType = (
  allowedAccountTypesConfig,
  currentUserAccountType
) => {
  const normalizedCurrentUserAccountTypes = new Set(
    normalizeAccountTypesArray(
      Array.isArray(currentUserAccountType)
        ? currentUserAccountType
        : [currentUserAccountType]
    ).map(accountType => accountType.toLowerCase())
  )

  if (Array.isArray(allowedAccountTypesConfig)) {
    return allowedAccountTypesConfig.some(allowedType =>
      normalizedCurrentUserAccountTypes.has(allowedType.toLowerCase())
    )
  }

  return normalizedCurrentUserAccountTypes.has(
    allowedAccountTypesConfig.toLowerCase()
  )
}

const getCurrentUserAccountTypes = accounts => {
  if (Array.isArray(accounts)) {
    return normalizeAccountTypesArray(
      accounts.map(account => account?.type || account?.accountType)
    )
  }

  return normalizeAccountTypesArray([accounts?.type || accounts?.accountType])
}

/**
 * Checks if the current user's account type is eligible for legal address display.
 *
 * This method enforces a two-level gate:
 * 1. `legalAddressCheckinCheckout` must be enabled (master switch) — read from store properties
 * 2. User's account type must be in the `legalAddressAllowedAccountTypes` list from store properties
 *
 * Uses the same property-lookup pattern as `isToShowLegalAddressChanges()` to fetch
 * configuration from activeStoreResponse or globalStoreResponse.
 *
 * Used by both legal address accordion and radio button components.
 *
 * @returns {boolean} True if user is eligible to see legal address UI, false otherwise.
 */
export const isAccountTypeEligibleForLegalAddress = () => {
  /** Master switch: if disabled, legal address is hidden from all account types */
  const isLegalAddressCheckoutEnabled = convertToBoolean(
    findLegalAddressPropertyValue('legalAddressCheckinCheckout', false)
  )

  if (!isLegalAddressCheckoutEnabled) {
    return false
  }

  /** Get allowed account types from store properties (stringified array, comma-separated string, or single string) */
  const allowedAccountTypes = findLegalAddressPropertyValue(
    'legalAddressAllowedAccountTypes',
    ''
  )
  const allowedAccountTypesConfig =
    parseAllowedAccountTypesConfig(allowedAccountTypes)

  /** If allowedAccountTypes is empty/not set, deny access (whitelist approach) */
  if (isAllowedAccountTypesConfigEmpty(allowedAccountTypesConfig)) {
    return false
  }

  /** Get current user's account type */
  const currentUserAccountTypes = getCurrentUserAccountTypes(
    customerContainer?.profileResponse?.accounts
  )

  /** If user type is not set, deny access */
  if (currentUserAccountTypes.length === 0) {
    return false
  }

  return isMatchingAllowedAccountType(
    allowedAccountTypesConfig,
    currentUserAccountTypes
  )
}
